import { Link as MUILink, toast, Typography } from '@montugroup/design-system';
import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import LoginFailure from '@/components/auth/login/components/LoginFailure';
import type { SignInFormData } from '@/components/auth/login/types';
import PatientLoginModal from '@/components/patientReferral/patientLoginModal';
import settings from '@/constants/constants';
import { FF_ENABLE_WOM_CAMPAIGN } from '@/constants/featureFlags';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { Logger } from '@/utils/logger';
import { CodeInputDialog } from '@montugroup/circuit-shared/components/common/CodeInputDialog';
import { IcebergPapayaContained, PapayaContained } from '@montugroup/circuit-shared/components/common/Papaya';
import { UserService } from '@montugroup/circuit-shared/services/user.service';

import {
  AuthCardContainer,
  AuthPageContainer,
  CardHalfContent,
  CardHalfDecoration,
  FormContainer,
  MobileLogoContainer,
} from '@/components/auth/authPage';
import logo from '@/components/layout/alternaleaf.svg';

import usePageMetadata from '@/hooks/usePageMetadata';
import {
  FORGOT_PASSWORD_ROUTE,
  HOME_ROUTE,
  LOGIN_ROUTE,
  LOGOUT_ROUTE,
  PROFILE_DETAILS_ROUTE,
} from '@/routes/route-paths';
import { zodResolver } from '@hookform/resolvers/zod';
import { HttpStatus } from '@montugroup/apis-styleguide';
import type { apiV1Contract } from '@montugroup/circuit-api-contracts';
import { UserPasswordSchema } from '@montugroup/circuit-api-contracts';
import { ShowPasswordAdornment, useAuthCheck, useLogin, zodEmailValidate } from '@montugroup/circuit-shared';
import { getTsApiV1Client } from '@montugroup/circuit-shared/api/api-v1.client';
import { PasswordRequirements } from '@montugroup/circuit-shared/components/common/PasswordRequirements';
import type { MontuError } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { isMontuError } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { isErrorResponse, TsRestResponseError } from '@ts-rest/core';
import { z } from 'zod';
import {
  AuthCardBackground,
  ErrorText,
  ForgotPasswordContainer,
  FormControl,
  FormEndAdornment,
  FormLabel,
  FormTitle,
  HelpTextContainer,
  MobileOnly,
  PageContainer,
  PatientInfoContainer,
  StyledFormGroup,
  StyledLoadingButton,
} from './SignIn.styles';

const logger = new Logger('PatientLogInForm');

const contactLink = 'https://www.alternaleaf.com.au/contact-us';

const pageTitle = 'Log in to your Alternaleaf patient portal';
const pageDescription =
  'Refill your prescription or book a follow-up appointment with your doctor to discuss or make changes to your treatment plan.';

const client = getTsApiV1Client();

function SignIn() {
  useAuthCheck({ defaultAuthedUserRoute: HOME_ROUTE });
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const onBoardKey = params?.onBoardKey || null;
  const { login, isPending, mfaController } = useLogin();
  const {
    mutateAsync: registerPassword,
    isPending: registerPasswordIsPending,
    contractEndpoint,
  } = client.user.registerPassword.useMutation();

  usePageMetadata({
    title: pageTitle,
    'og:title': pageTitle,
    description: pageDescription,
    'og:description': pageDescription,
  });

  const [isPasswordRevealed, setIsPasswordRevealed] = useState<boolean>(false);
  const [isConfirmPasswordRevealed, setIsConfirmPasswordRevealed] = useState<boolean>(false);
  const formSchema = z
    .object({
      password: onBoardKey ? UserPasswordSchema : z.string().min(1, { message: 'You must enter a password' }),
      email: z.string().min(1, { message: 'You must enter an email address' }).email('Invalid email address'),
      ...(onBoardKey ? { rePassword: z.string().min(1, { message: 'Please confirm your password' }) } : {}),
    })
    .superRefine((values, ctx) => {
      if (onBoardKey && values.rePassword !== values.password) {
        ctx.addIssue({
          message: 'Passwords do not match',
          code: 'custom',
          path: ['rePassword'],
        });
      }
    });

  const { handleSubmit, formState, setValue, control } = useForm<SignInFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      rePassword: '',
    },
  });
  const { errors } = formState;
  const [submissionError, setSubmissionError] = useState<string | MontuError<typeof apiV1Contract.user.login>>('');
  const { flags } = useFeatureFlags();
  const [isOnboarding, setIsOnboarding] = useState<boolean>(onBoardKey !== null);

  useEffect(() => {
    (async () => {
      if (isOnboarding && onBoardKey) {
        try {
          const { data } = await UserService.getUserOnboardEmail(onBoardKey);
          if (!data) {
            toast.warn('Invalid key. Please try again!');
            navigate(LOGOUT_ROUTE);
            return;
          }
          if (data.expired) {
            toast.warn('The link has expired. Please reset your password.');
            navigate(FORGOT_PASSWORD_ROUTE);
            return;
          }

          setValue('email', data.email);
        } catch {
          toast.error('Unexpected error. Please retry later.');
          navigate(LOGIN_ROUTE);
        }
      }
    })();
  }, [onBoardKey, isOnboarding, setValue, navigate]);

  const onRegister: SubmitHandler<SignInFormData> = async (formData) => {
    try {
      if (!onBoardKey) {
        throw new Error('No onBoardKey');
      }

      const response = await registerPassword({
        body: {
          email: formData.email,
          token: onBoardKey,
          password: formData.password,
        },
        query: {},
      });

      if (response.status === HttpStatus.OK) {
        setIsOnboarding(false);
        setValue('password', '');
        setValue('rePassword', '');

        toast.success('Registered Successfully. Please Login now.');
        navigate(LOGIN_ROUTE, { replace: true });
      } else {
        throw new TsRestResponseError(contractEndpoint, response);
      }
    } catch (error) {
      if (isErrorResponse(error, contractEndpoint) && isMontuError(error)) {
        if (error.body.error.code === 'password.requirements_not_met') {
          toast.error(
            'The password you entered is invalid as it does not meet our security requirements. Please choose a different password.',
          );
        } else if (error.body.error.message) {
          toast.error(error.body.error.message);
        }
      } else {
        toast.error('Error Occurred! Try again later!');
      }

      logger.error('handleRegister', error);
    }
  };

  const onSubmit: SubmitHandler<SignInFormData> = async (formData) => {
    setSubmissionError('');
    const userFrom = location?.state?.from?.pathname || '';
    const userFromState = location?.state?.from?.state || {};
    let user;

    try {
      user = await login({ email: formData.email.toLowerCase().trim(), password: formData.password });
    } catch (err) {
      logger.info('AuthService.login failed: ', err);
      setSubmissionError(err as string);
    }

    if (!user || !('user' in user)) {
      return;
    }

    if (!user.user?.phone) {
      logger.info('navigating to profile detail');
      navigate(PROFILE_DETAILS_ROUTE, {
        replace: true,
      });
      toast.success('Complete your profile details');
      return;
    } else if (userFrom && !userFrom.includes('logout')) {
      logger.info('navigating to: ', userFrom);
      navigate(
        {
          pathname: userFrom,
        },
        {
          replace: true,
          state: userFromState,
        },
      );
    } else {
      logger.info('navigating to /home');

      navigate(HOME_ROUTE, {
        replace: true,
      });
    }
  };

  return (
    <PageContainer>
      <AuthPageContainer>
        <AuthCardContainer>
          <CardHalfDecoration>
            <AuthCardBackground>
              <PapayaContained />
            </AuthCardBackground>
          </CardHalfDecoration>
          <CardHalfContent>
            <MobileLogoContainer>
              <MUILink href={settings.marketing.site.url} underline="none" target="_blank" rel="noopener noreferrer">
                <Box component="img" src={logo} alt="Alternaleaf" sx={{ px: 6, py: 2 }} />
              </MUILink>
            </MobileLogoContainer>
            <FormContainer>
              <LoginFailure error={submissionError} showSupportText />
              <Box
                component="form"
                data-private
                onSubmit={onBoardKey ? handleSubmit(onRegister) : handleSubmit(onSubmit)}
                noValidate
              >
                <FormTitle variant="h2">
                  {onBoardKey ? 'Set a password for your account' : 'Log in to your patient portal'}
                </FormTitle>
                {onBoardKey && (
                  <Typography sx={(theme) => ({ marginBottom: theme.spacing(4) })}>
                    Please enter your password below. Your password must:
                    <PasswordRequirements isFirstPassword />
                  </Typography>
                )}
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'You must enter an email address',
                    validate: zodEmailValidate,
                  }}
                  render={({ field }) => (
                    <StyledFormGroup>
                      <FormLabel htmlFor="useremailone">{onBoardKey ? 'Your email' : 'Patient Login'}</FormLabel>
                      <FormControl
                        {...field}
                        id="useremailone"
                        type="email"
                        placeholder="Your Email Address"
                        autoComplete="email"
                        disabled={!!onBoardKey}
                        aria-invalid={!!errors.email}
                      />
                      {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
                    </StyledFormGroup>
                  )}
                />
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <StyledFormGroup>
                      <FormLabel htmlFor="userpasswordone">{onBoardKey ? 'Enter new password' : 'Password'}</FormLabel>
                      <FormControl
                        {...field}
                        id="userpasswordone"
                        type={isPasswordRevealed ? 'text' : 'password'}
                        placeholder="Enter password"
                        aria-invalid={!!errors.password}
                      />
                      <FormEndAdornment>
                        <ShowPasswordAdornment isRevealed={isPasswordRevealed} toggle={setIsPasswordRevealed} />
                      </FormEndAdornment>
                      {errors.password && <ErrorText>{errors.password.message}</ErrorText>}
                    </StyledFormGroup>
                  )}
                />

                {onBoardKey && (
                  <Controller
                    name="rePassword"
                    control={control}
                    rules={{
                      required: 'Please confirm your password',
                      validate: (val, formData) => val === formData.password || 'Passwords do not match',
                    }}
                    render={({ field }) => (
                      <StyledFormGroup>
                        <FormLabel htmlFor="re-password">Confirm new password</FormLabel>
                        <FormControl
                          {...field}
                          id="re-password"
                          type={isConfirmPasswordRevealed ? 'text' : 'password'}
                          placeholder="Enter password"
                          aria-invalid={!!errors.rePassword}
                        />
                        <FormEndAdornment>
                          <ShowPasswordAdornment
                            isRevealed={isConfirmPasswordRevealed}
                            toggle={setIsConfirmPasswordRevealed}
                          />
                        </FormEndAdornment>

                        {errors.rePassword && <ErrorText>{errors.rePassword.message}</ErrorText>}
                      </StyledFormGroup>
                    )}
                  />
                )}

                <ForgotPasswordContainer>
                  <Link to={FORGOT_PASSWORD_ROUTE}>Forgot Password?</Link>
                </ForgotPasswordContainer>

                <StyledLoadingButton
                  type="submit"
                  id="btnSubmit"
                  variant="contained"
                  color="primary"
                  loading={isPending || registerPasswordIsPending}
                  data-testid="login-button"
                >
                  {onBoardKey ? 'Set password' : 'Login'}
                </StyledLoadingButton>
              </Box>

              <HelpTextContainer>
                <Typography variant="body1">
                  Do you need help?&nbsp;
                  <MUILink href={contactLink} underline="none">
                    Contact us
                  </MUILink>
                </Typography>
              </HelpTextContainer>
              <PatientInfoContainer>
                <Typography variant="body1">
                  Not a patient?&nbsp;
                  <MUILink
                    href={settings.marketing.site.url}
                    underline="none"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Find out more about Alternaleaf
                  </MUILink>
                </Typography>
              </PatientInfoContainer>
              {flags[FF_ENABLE_WOM_CAMPAIGN] && <PatientLoginModal />}
            </FormContainer>
          </CardHalfContent>
        </AuthCardContainer>
        <MobileOnly>
          <IcebergPapayaContained sx={{ position: 'fixed' }} />
        </MobileOnly>
      </AuthPageContainer>
      <CodeInputDialog
        controller={mfaController}
        helpLink="https://support.alternaleaf.com.au/hc/en-au/articles/14441372555407-Multi-factor-Authentication"
      />
    </PageContainer>
  );
}

export default SignIn;
